import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useVisitor } from "@/App";
import axios from "axios";
import { Play, Lock, Loader2 } from "lucide-react";

export default function SharedAccess() {
  const { token } = useParams();
  const { loginVisitor, API } = useVisitor();
  const navigate = useNavigate();
  const [status, setStatus] = useState("validating"); // validating | invalid | expired
  const [errorDetail, setErrorDetail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/share-links/validate/${encodeURIComponent(token)}`);
        if (cancelled) return;
        // Authorize this visitor without going through the form
        loginVisitor({
          id: `share-${token}`,
          name: data.label || "Shared Access",
          company: "—",
          email: "",
          phone: "",
          shared: true,
          shared_label: data.label || "",
        });
        navigate("/raw-footage", { replace: true });
      } catch (err) {
        if (cancelled) return;
        const code = err.response?.status;
        if (code === 410) setStatus("expired");
        else setStatus("invalid");
        setErrorDetail(err.response?.data?.detail || "");
      }
    })();
    return () => { cancelled = true; };
  }, [token, API, loginVisitor, navigate]);

  return (
    <div className="visitor-gate-bg flex items-center justify-center px-4" data-testid="shared-access-page">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 mb-8">
          <Play className="w-8 h-8 text-[#FF5A00] fill-[#FF5A00]" />
          <span className="text-3xl font-bold tracking-tight text-[#0A1128]" style={{ fontFamily: "Outfit" }}>
            HiFy
          </span>
        </div>

        {status === "validating" && (
          <div className="bg-white/95 backdrop-blur-xl p-10 rounded-2xl border border-[#0A1128]/10 shadow-[0_20px_60px_rgba(10,17,40,0.08)]" data-testid="shared-loading">
            <Loader2 className="w-10 h-10 text-[#FF5A00] animate-spin mx-auto mb-4" />
            <p className="text-[#0A1128]/70 font-medium">Verifying your private link…</p>
          </div>
        )}

        {status === "expired" && (
          <div className="bg-white/95 backdrop-blur-xl p-10 rounded-2xl border border-[#0A1128]/10 shadow-[0_20px_60px_rgba(10,17,40,0.08)]" data-testid="shared-expired">
            <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#0A1128] mb-2" style={{ fontFamily: "Outfit" }}>Link Expired</h1>
            <p className="text-[#0A1128]/60 text-sm mb-6">
              This private link is no longer active. Reach out to your HiFy contact for a fresh one.
            </p>
            <a
              href="mailto:harsh.shah@hify.club"
              className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white text-sm font-bold px-4 py-2 rounded-lg transition-all"
              data-testid="shared-contact-btn"
            >
              Contact Us
            </a>
          </div>
        )}

        {status === "invalid" && (
          <div className="bg-white/95 backdrop-blur-xl p-10 rounded-2xl border border-[#0A1128]/10 shadow-[0_20px_60px_rgba(10,17,40,0.08)]" data-testid="shared-invalid">
            <Lock className="w-12 h-12 text-[#0A1128]/40 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#0A1128] mb-2" style={{ fontFamily: "Outfit" }}>Invalid Link</h1>
            <p className="text-[#0A1128]/60 text-sm mb-6">
              {errorDetail || "We couldn't recognise this link. Double-check the URL or request a new one."}
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 bg-[#0A1128] hover:bg-[#0A1128]/80 text-white text-sm font-bold px-4 py-2 rounded-lg transition-all"
              data-testid="shared-go-home-btn"
            >
              Go to Home
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
